// Smooth lines through measured points: the monotone cubic of d3's
// curveMonotoneX. It never overshoots — a curve between two equal days stays
// flat, and a rate never dips below zero between two points — which is why it
// is the one smoothing the charts use.

export type Point = readonly [number, number];

function slope(a: Point, b: Point): number {
  const dx = b[0] - a[0];
  return dx === 0 ? 0 : (b[1] - a[1]) / dx;
}

function tangents(points: readonly Point[]): number[] {
  const n = points.length;
  if (n < 2) return points.map(() => 0);
  const secants: number[] = [];
  for (let index = 0; index < n - 1; index += 1) {
    secants.push(slope(points[index] as Point, points[index + 1] as Point));
  }
  const result: number[] = [secants[0] ?? 0];
  for (let index = 1; index < n - 1; index += 1) {
    const before = secants[index - 1] ?? 0;
    const after = secants[index] ?? 0;
    if (before * after <= 0) {
      result.push(0);
    } else {
      const h0 = (points[index] as Point)[0] - (points[index - 1] as Point)[0];
      const h1 = (points[index + 1] as Point)[0] - (points[index] as Point)[0];
      const p = (before * h1 + after * h0) / (h0 + h1);
      const limit = 2 * Math.min(Math.abs(before), Math.abs(after));
      result.push(Math.sign(before) * Math.min(Math.abs(p), limit));
    }
  }
  result.push(secants[n - 2] ?? 0);
  return result;
}

/** The SVG path of a smooth line through `points` (sorted by x). With
 * `move` false the path continues the current one (an area's far side). */
export function smoothPath(points: readonly Point[], move = true): string {
  if (points.length === 0) return "";
  const first = points[0] as Point;
  const parts = [`${move ? "M" : "L"}${first[0]},${first[1]}`];
  if (points.length === 1) return parts.join("");
  const t = tangents(points);
  for (let index = 0; index < points.length - 1; index += 1) {
    const a = points[index] as Point;
    const b = points[index + 1] as Point;
    const third = (b[0] - a[0]) / 3;
    const c1: Point = [a[0] + third, a[1] + (t[index] ?? 0) * third];
    const c2: Point = [b[0] - third, b[1] - (t[index + 1] ?? 0) * third];
    parts.push(`C${c1[0]},${c1[1]} ${c2[0]},${c2[1]} ${b[0]},${b[1]}`);
  }
  return parts.join("");
}
